import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useQuery, useUpdateQuery } from '../../hooks/urlHooks';
import { ContactSupportDialog } from '../support';
import { preloadChatHistory } from '../support/ai-assistant/chatPreloadSlice';

/**
 * URLDialogManager
 *
 * Manages dialogs/popups based on URL query parameters.
 * Enables shareable URLs that open specific dialogs while preserving the underlying page.
 *
 * Usage:
 * - Add ?dialog=contact-support to any URL to open the contact support dialog
 * - Dialog closes by removing the query parameter from URL
 * - Works with browser back/forward navigation
 * - Preserves dialog state on page refresh
 *
 * Examples:
 * - /#/library/team123/folder456?dialog=contact-support
 * - /#/settings/user?dialog=contact-support
 * - /?dialog=contact-support (auto-redirects to library with dialog)
 */
const URLDialogManager: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { dialog } = useQuery();
  const updateQuery = useUpdateQuery();

  // Clean up window query params after they've been processed
  useEffect(() => {
    const windowParams = new URLSearchParams(window.location.search);
    const dialogFromWindow = windowParams.get('dialog');

    // If there's a dialog param in window.location.search, we need to clean it up
    // because it should only be in the hash part of the URL
    if (dialogFromWindow && location.pathname !== '/') {
      // Remove dialog param from window.location.search
      const cleanWindowParams = new URLSearchParams(window.location.search);
      cleanWindowParams.delete('dialog');
      const cleanSearch = cleanWindowParams.toString();

      if (window.history.replaceState) {
        // Build clean URL: remove dialog from before hash
        const newUrl =
          window.location.pathname +
          (cleanSearch ? `?${cleanSearch}` : '') +
          window.location.hash;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [location.pathname]);

  // Preload chat history when contact-support dialog is opened via URL
  useEffect(() => {
    if (dialog === 'contact-support') {
      dispatch(preloadChatHistory());
    }
  }, [dialog, dispatch]);

  const handleCloseDialog = () => {
    // Remove the dialog parameter from URL
    updateQuery({ dialog: undefined });
  };

  // Render the appropriate dialog based on the URL parameter
  switch (dialog) {
    case 'contact-support':
      return (
        <ContactSupportDialog
          onClose={handleCloseDialog}
          formSource="URL - Contact Support"
        />
      );
    // Add more dialog cases here as needed
    // case 'invite-team':
    //   return <InviteTeamDialog onClose={handleCloseDialog} />;
    default:
      return null;
  }
};

export default URLDialogManager;
